/**
 * Shared UI helpers for the BDC portal.
 * Slightly darker chrome than the dealer portal — slate/navy accents.
 */
import React from "react";

export const PageHeader = ({ title, subtitle, right, testId }) => (
  <div
    className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6"
    data-testid={testId || "bdc-page-header"}
  >
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
        {title}
      </h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
  </div>
);

export const PageContainer = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 ${className}`}>
    {children}
  </div>
);

export const StatusPill = ({ tone = "slate", children, className = "" }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    gold: "bg-[#FCF3D9] text-[#8B6914] border-[#F1E1A8]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${tones[tone] || tones.slate} ${className}`}
    >
      {children}
    </span>
  );
};

export const LEAD_STATUS = {
  new: { label: "New", tone: "slate" },
  contacted: { label: "Contacted", tone: "blue" },
  followed_up: { label: "Followed Up", tone: "emerald" },
  lost: { label: "Lost", tone: "slate" },
  spam: { label: "Spam", tone: "rose" },
};

export const APPT_STATUS = {
  scheduled: { label: "Scheduled", tone: "amber" },
  confirmed: { label: "Confirmed", tone: "emerald" },
  showed_up: { label: "Showed Up", tone: "emerald" },
  no_show: { label: "No Show", tone: "rose" },
  cancelled: { label: "Cancelled", tone: "slate" },
  rescheduled: { label: "Rescheduled", tone: "blue" },
};

export const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-100 rounded-md ${className}`} />
);

export const relativeTime = (iso) => {
  if (!iso) return "—";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

export const waitingLabel = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `waiting ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `waiting ${hours}h`;
  const days = Math.floor(hours / 24);
  return `waiting ${days}d`;
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
};
